import { DeleteOutlined, EditOutlined } from "@ant-design/icons";
import { Button, Card, Dropdown, Input, Menu, Popover, Table } from "antd";
import { Fragment, useEffect, useState } from "react";
import { CSVLink } from "react-csv";
import { useDispatch, useSelector } from "react-redux";
import { Link, Navigate, useNavigate, useParams } from "react-router-dom";
import { toast } from "react-toastify";
import { DeleteBookPublisher } from "../../redux/actions/bookPublisher/deleteBookPublisherAction";
import { loadSingleBookPublisher } from "../../redux/actions/bookPublisher/detailBookPublisherAction";
import Loader from "../loader/loader";
import GenerateBarcodePopUp from "../product/generateBarcodePopUp";
import "../productCategory/productCategory.css";

const PUBLISHER_COLUMNS = [
  {
    title: "Image",
    dataIndex: "imageUrl",
    key: "imageUrl",
    render: (imageUrl) => (
      <img style={{ maxWidth: "40px" }} alt="product" src={imageUrl} />
    ),
  },
  { title: "SKU", dataIndex: "sku", key: "sku" },
  {
    title: "Name",
    dataIndex: "name",
    key: "name",
    render: (name, { id }) => <Link to={`/product/${id}`}>{name}</Link>,
  },
  { title: "Quantity", dataIndex: "quantity", key: "quantity" },
  { title: "Purchase price", dataIndex: "purchase_price", key: "purchase_price", responsive: ["md"] },
  { title: "Sale price", dataIndex: "sale_price", key: "sale_price", responsive: ["md"] },
  { title: "Unit Type", dataIndex: "unit_type", key: "unit_type" },
  { title: "Reorder QTY", dataIndex: "reorder_quantity", key: "reorder_quantity" },
  {
    title: "Action",
    key: "action",
    render: (_, record) => <GenerateBarcodePopUp sku={record.sku ? record.sku : 0} />,
  },
];

const TABLE_SCROLL_Y = 360;

function ProductTable({ list, columnsToShow }) {
  const addKeys = (arr) => arr.map((i) => ({ ...i, key: i.id }));

  const hasCol = (key) => Array.isArray(columnsToShow) && columnsToShow.some((c) => c.key === key);

  return (
    <div className="pcat-product-list-card">
      {/* Desktop: keep table */}
      <div className="detail-page-table-wrap d-none d-lg-block">
        <Table
          loading={!list}
          columns={columnsToShow}
          dataSource={list ? addKeys(list) : []}
          size="small"
          scroll={{ y: TABLE_SCROLL_Y }}
        />
      </div>

      {/* Mobile: render cards */}
      <div className="pcat-product-mobile-cards d-lg-none">
        {list && list.length > 0 ? (
          <div className="pcat-product-mobile-list">
            {list.map((record, idx) => (
              <div key={record.id ?? idx} className="pcat-product-mobile-card">
                <div className="pcat-product-mobile-card-body">
                  {hasCol("imageUrl") && record.imageUrl ? (
                    <img
                      className="pcat-product-mobile-image"
                      src={record.imageUrl}
                      alt="product"
                    />
                  ) : null}

                  <div className="pcat-product-mobile-main">
                    {hasCol("name") && (
                      <div className="pcat-product-mobile-name">
                        <Link to={`/product/${record.id}`}>{record.name || "—"}</Link>
                      </div>
                    )}

                    {hasCol("sku") && (
                      <div className="pcat-product-mobile-line">
                        <span className="pcat-product-mobile-label">SKU</span>
                        <span className="pcat-product-mobile-value">{record.sku || "—"}</span>
                      </div>
                    )}

                    {hasCol("quantity") && (
                      <div className="pcat-product-mobile-line">
                        <span className="pcat-product-mobile-label">Qty</span>
                        <span className="pcat-product-mobile-value">{record.quantity ?? "—"}</span>
                      </div>
                    )}

                    {hasCol("purchase_price") && (
                      <div className="pcat-product-mobile-line">
                        <span className="pcat-product-mobile-label">Purchase</span>
                        <span className="pcat-product-mobile-value">
                          {record.purchase_price ?? "—"}
                        </span>
                      </div>
                    )}

                    {hasCol("sale_price") && (
                      <div className="pcat-product-mobile-line">
                        <span className="pcat-product-mobile-label">Sale</span>
                        <span className="pcat-product-mobile-value">{record.sale_price ?? "—"}</span>
                      </div>
                    )}

                    {hasCol("unit_type") && (
                      <div className="pcat-product-mobile-line">
                        <span className="pcat-product-mobile-label">Unit</span>
                        <span className="pcat-product-mobile-value">{record.unit_type || "—"}</span>
                      </div>
                    )}

                    {hasCol("reorder_quantity") && (
                      <div className="pcat-product-mobile-line">
                        <span className="pcat-product-mobile-label">Reorder</span>
                        <span className="pcat-product-mobile-value">
                          {record.reorder_quantity ?? "—"}
                        </span>
                      </div>
                    )}
                  </div>

                  {hasCol("action") && (
                    <div className="pcat-product-mobile-action">
                      <GenerateBarcodePopUp sku={record.sku ? record.sku : 0} />
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="text-center py-5 pcat-product-mobile-empty">
            No books found
          </div>
        )}
      </div>
    </div>
  );
}

const DetailBookPublisher = () => {
  const { id } = useParams();
  let navigate = useNavigate();
  const dispatch = useDispatch();
  const publisher = useSelector((state) => state.bookPublishers.publisher);

  const [columnItems, setColumnItems] = useState([]);
  const [columnsToShow, setColumnsToShow] = useState(PUBLISHER_COLUMNS);
  const [productSearch, setProductSearch] = useState("");

  useEffect(() => {
    const items = PUBLISHER_COLUMNS.map((item) => ({
      key: item.key,
      label: <span>{item.title}</span>,
    }));
    setColumnItems(items);
    setColumnsToShow(PUBLISHER_COLUMNS);
  }, []);

  const colVisibilityClickHandler = (col) => {
    const ifColFound = columnsToShow.find((item) => item.key === col.key);
    if (ifColFound) {
      setColumnsToShow((prev) => prev.filter((item) => item.key !== col.key));
    } else {
      const foundIndex = PUBLISHER_COLUMNS.findIndex((item) => item.key === col.key);
      const foundCol = PUBLISHER_COLUMNS.find((item) => item.key === col.key);
      setColumnsToShow((prev) => {
        const next = [...prev];
        next.splice(foundIndex, 0, foundCol);
        return next;
      });
    }
  };

  const onDelete = () => {
    try {
      dispatch(DeleteBookPublisher(id));
      setVisible(false);
      toast.warning(`Publisher : ${publisher.name} is removed `);
      return navigate("/book-publisher");
    } catch (error) {
      console.log(error.message);
    }
  };

  const [visible, setVisible] = useState(false);
  const handleVisibleChange = (newVisible) => setVisible(newVisible);

  useEffect(() => {
    dispatch(loadSingleBookPublisher(id));
  }, [id]);

  const isLogged = Boolean(localStorage.getItem("isLogged"));
  if (!isLogged) {
    return <Navigate to={"/auth/login"} replace={true} />;
  }

  return (
    <div className="pcat-detail-page">
      <div className="pcat-detail-wrap">
        {publisher ? (
          <Fragment key={publisher.id}>
            <Card bordered={false} className="pcat-detail-card">
              <div className="pcat-detail-header">
                <h5 className="pcat-detail-title">
                  <i className="bi bi-person-lines-fill" aria-hidden />
                  <span className="pcat-detail-name">{publisher.name}</span>
                </h5>
                <div className="pcat-detail-actions">
                  <Link
                    to={`/book-publisher/${publisher.id}/update`}
                    state={{ data: publisher }}>
                    <Button type="primary" shape="round" icon={<EditOutlined />} aria-label="Edit publisher" />
                  </Link>
                  <Popover
                    content={
                      <a onClick={onDelete}>
                        <Button type="primary" danger>Yes Please !</Button>
                      </a>
                    }
                    title="Are you sure you want to delete ?"
                    trigger="click"
                    visible={visible}
                    onVisibleChange={handleVisibleChange}>
                    <Button type="danger" shape="round" icon={<DeleteOutlined />} aria-label="Delete publisher" />
                  </Popover>
                  {publisher?.product && (
                    <>
                      <Dropdown
                        overlay={<Menu onClick={colVisibilityClickHandler} items={columnItems} />}
                        placement="bottomRight"
                      >
                        <Button size="small" className="column-visibility">Column Visibility</Button>
                      </Dropdown>
                      <CSVLink
                        data={publisher.product}
                        className="btn btn-dark btn-sm"
                        filename={`publisher_${publisher.name}`}>
                        Download CSV
                      </CSVLink>
                    </>
                  )}
                </div>
              </div>

              <div className="pcat-products-section">
                <div className="pcat-products-section-header">
                  <h5 className="pcat-products-heading">Books under {publisher.name}</h5>
                  <Input
                    placeholder="Search products (name, SKU)"
                    allowClear
                    size="small"
                    value={productSearch}
                    onChange={(e) => setProductSearch(e.target.value)}
                    className="pcat-products-search"
                  />
                </div>
                <ProductTable
                  list={
                    publisher?.product?.filter(
                      (p) =>
                        !productSearch ||
                        (p.name && p.name.toLowerCase().includes(productSearch.toLowerCase())) ||
                        (p.sku && String(p.sku).toLowerCase().includes(productSearch.toLowerCase()))
                    )
                  }
                  columnsToShow={columnsToShow}
                />
              </div>
            </Card>
          </Fragment>
        ) : (
          <Loader />
        )}
      </div>
    </div>
  );
};

export default DetailBookPublisher;
